import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// local imports && assets
import successImage from 'assets/images/success-image.svg'
import calendarLoading from 'assets/icons/loading-calendar.png'
import useDidMountEffect from 'utils/useDidMountEffect'
import AuthUtil from 'utils/AuthUtil'
import DateUtil from 'utils/DateUtil'
import { IFilteredMeetingRoomList, IMeetingRoomFilteredReq } from 'types/MeetingRoomType'
import useGetMeetingRoomList from '../hooks/use-get-meeting-room-list'
import useGetMeetingLocationList from '../hooks/use-get-meeting-room-locations'
import useMeetingRoomResModal from '../hooks/use-meeting-room-res-modal'
import ReservationModalWrapper from '../reservation-scheduler/ReservationModal'
import MeetingRoomSearch from './MeetingRoomSearch'
import MeetingRoomCard from './MeetingRoomCard'
import MeetingRoomFilterForm from './MeetingRoomFilterForm'
import Loader from 'components/ui/Loader'
import NoContent from 'components/ui/NoContent'
import NotificationDialog from 'components/ui/NotificationDialog'
import { Divider, FormLayoutWrapper, Line, RoomCardList } from './styled'

/* filtered date - default */
const TODAY = DateUtil.getStartOfDay(new Date())

export default function MeetingRoomList() {
  const history = useHistory()
  const { t } = useTranslation()
  const [locationIds, setLocationIds] = useState<number[] | []>([])

  /* Get Current logged employee infos - (location id) */
  const { data: locationList, isLoading } = useGetMeetingLocationList()

  useEffect(() => {
    const employeeLocation = AuthUtil.getEmployeeSummary()?.locationId || 0
    const empLocationIsExist = locationList?.map((o: any) => o.id).includes(employeeLocation)
    setLocationIds(() => (empLocationIsExist ? [employeeLocation] : []))
    setValue('locationIds', empLocationIsExist ? [employeeLocation] : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationList])

  /* Filter form default values */
  const defaultValues: IMeetingRoomFilteredReq = { locationIds: locationIds, reservationDate: TODAY }

  /*  Search and filter from Meeting Room list */
  const [searchText, setSearchText] = useState('')
  const [filteredRoom, setFilteredRoom] = useState<IFilteredMeetingRoomList[] | []>([])

  /* Filter form state  */
  const { control, reset, handleSubmit, formState, getValues, setValue, watch } = useForm<IMeetingRoomFilteredReq>({
    defaultValues,
  })
  const filteredDate = watch('reservationDate') || TODAY

  /* Get Meeting Room List - Locations | Custom Hooks */
  const { getMeetingRoomList, meetingRooms, loading } = useGetMeetingRoomList()

  /* Form Actions | Methods | button disabled states */
  const { isDirty, submitCount } = formState
  const disableResetBtn = !isDirty && submitCount < 1
  const disableSubmitBtn = !isDirty && submitCount < 1

  /* Handle Form Submit */
  const onSubmit = (data: IMeetingRoomFilteredReq) => getMeetingRoomList(data)
  const runAfterSubmit = () => getMeetingRoomList(getValues())

  /* Handle Reset Filter Form */
  const handleResetForm = () => {
    reset(
      { locationIds: locationIds, reservationDate: TODAY },
      { keepDirtyValues: false, keepDirty: false, keepDefaultValues: false }
    )
    !!submitCount && onSubmit(defaultValues)
    setSearchText('')
  }

  /* Get Meeting Room List on initial page load  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useDidMountEffect(() => onSubmit(defaultValues), [isLoading])

  /* Render MeetingRooms or filtered Meeting Room list */
  const renderList = searchText ? filteredRoom : meetingRooms

  const { modal: isOpen, openModal, closeModal, notification, setNotification, selectedRoom } = useMeetingRoomResModal()

  const closeNotification = () => {
    runAfterSubmit()
    setNotification(false)
  }

  /* Show full-screen loader only on initial load (when list is empty).
     During background refresh after a reservation, keep cards/notification mounted. */
  if ((loading || isLoading) && !meetingRooms.length) return <ListLoader />

  return (
    <div className="campus-services">
      {/* Filter Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLayoutWrapper>
          <MeetingRoomSearch
            rooms={meetingRooms}
            setData={setFilteredRoom}
            searchText={searchText}
            setSearchText={setSearchText}
          />

          <Line className="pi pi-ellipsis-h" />
          <MeetingRoomFilterForm
            control={control}
            locationList={locationList}
            disableResetBtn={disableResetBtn}
            disableSubmitBtn={disableSubmitBtn}
            handleReset={handleResetForm}
          />
        </FormLayoutWrapper>
      </form>

      <Divider />

      {/* Card List — keep mounted during refresh so the success notification
          dialog (rendered below) is not unmounted. */}
      <RoomCardList>
        {!!renderList?.length ? (
          renderList?.map((room) => <MeetingRoomCard openModal={openModal} key={room.id} room={room} />)
        ) : (
          <NoContent />
        )}
      </RoomCardList>

      {/* Meeting Room Reservation Scheduler Modal */}
      {!!selectedRoom ? (
        <ReservationModalWrapper
          open={isOpen}
          openModal={openModal}
          closeModal={closeModal}
          showNotification={() => {
            runAfterSubmit()
            setNotification(true)
          }}
          selectedRoom={selectedRoom}
          filterDate={filteredDate}
        />
      ) : null}

      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('meeting-res-modal.notification-title')}
        content={t('meeting-res-modal.notification-text')}
        firstButtonLabel={t('buttons.go-to-calendar')}
        onHide={closeNotification}
        onSubmit={() => history.push('/calendar')}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </div>
  )
}

function ListLoader() {
  return (
    <div className="py-5 my-4">
      <Loader src={calendarLoading} />
    </div>
  )
}
